import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getActiveEvent, getEventById, selectableEvents, type EventId, type EventThemeConfig } from '@/configs/event-theme';

const DEV_OVERRIDE_KEY = 'suigar.event.override';

interface EventThemeContextValue {
	event: EventThemeConfig;
	override: EventId | 'auto';
	setOverride: (id: EventId | 'auto') => void;
	availableEvents: { id: EventId | 'auto'; name: string }[];
}

const EventThemeContext = createContext<EventThemeContextValue | null>(null);

function resolveInitialOverride(): EventId | 'auto' {
	if (!import.meta.env.DEV) return 'auto';
	if (typeof window === 'undefined') return 'auto';
	const params = new URLSearchParams(window.location.search);
	const queryOverride = params.get('event');
	if (queryOverride && getEventById(queryOverride)) {
		return queryOverride as EventId;
	}
	const stored = window.localStorage.getItem(DEV_OVERRIDE_KEY);
	if (stored && getEventById(stored)) {
		return stored as EventId;
	}
	return 'auto';
}

function computeEvent(override: EventId | 'auto'): EventThemeConfig {
	if (override !== 'auto') {
		const event = getEventById(override);
		if (event) return event;
	}
	return getActiveEvent();
}

export function EventThemeProvider({ children }: { children: React.ReactNode }) {
	const [override, setOverrideState] = useState<EventId | 'auto'>(resolveInitialOverride);

	const event = useMemo(() => computeEvent(override), [override]);

	useEffect(() => {
		document.documentElement.setAttribute('data-event', event.id);
	}, [event.id]);

	const setOverride = useCallback((id: EventId | 'auto') => {
		setOverrideState(id);
		if (import.meta.env.DEV && typeof window !== 'undefined') {
			if (id === 'auto') {
				window.localStorage.removeItem(DEV_OVERRIDE_KEY);
			} else {
				window.localStorage.setItem(DEV_OVERRIDE_KEY, id);
			}
		}
	}, []);

	useEffect(() => {
		if (!import.meta.env.DEV || typeof window === 'undefined') return;
		const handler = (ev: KeyboardEvent) => {
			if (ev.key?.toLowerCase() === 'e' && (ev.ctrlKey || ev.metaKey)) {
				ev.preventDefault();
				const currentIndex = debugOptions.findIndex((opt) => opt.id === override);
				const next = debugOptions[(currentIndex + 1) % debugOptions.length];
				setOverride(next.id as EventId | 'auto');
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [override, setOverride]);

	const value = useMemo<EventThemeContextValue>(
		() => ({
			event,
			override,
			setOverride,
			availableEvents: debugOptions,
		}),
		[event, override, setOverride],
	);

	return (
		<EventThemeContext.Provider value={value}>
			{children}
			{import.meta.env.DEV && <EventThemeDevPanel />}
		</EventThemeContext.Provider>
	);
}

const debugOptions: { id: EventId | 'auto'; name: string }[] = [{ id: 'auto', name: 'Auto (calendar)' }, ...selectableEvents];

function EventThemeDevPanel() {
	const ctx = useEventTheme();
	const [open, setOpen] = useState(false);

	return null;
	if (!import.meta.env.DEV) return null;

	return (
		<div className="fixed bottom-4 right-4 z-[9999]">
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="rounded-full bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-lg shadow-primary/40"
			>
				Event theme: {ctx.override === 'auto' ? ctx.event.name : ctx.availableEvents.find((opt) => opt.id === ctx.override)?.name}
			</button>
			{open && (
				<div className="mt-2 w-56 rounded-lg border border-border bg-popover p-3 shadow-lg">
					<p className="mb-2 text-xs font-semibold text-muted-foreground">Preview event appearance</p>
					<select
						value={ctx.override}
						onChange={(event) => ctx.setOverride(event.target.value as EventId | 'auto')}
						className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus-visible:outline-none"
					>
						{ctx.availableEvents.map((opt) => (
							<option key={opt.id} value={opt.id}>
								{opt.name}
							</option>
						))}
					</select>
					<p className="mt-2 text-[10px] text-muted-foreground">Shortcut: Ctrl/Cmd + E cycles through events.</p>
				</div>
			)}
		</div>
	);
}

export function useEventTheme(): EventThemeContextValue {
	const ctx = useContext(EventThemeContext);
	if (!ctx) {
		throw new Error('useEventTheme must be used within EventThemeProvider');
	}
	return ctx;
}
